import {Card, Flag, Placement, useAnimatedPopper} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useDateTimeFormatter} from 'platform/locale';
import {Area, AreaChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid} from 'recharts';
import styled, {useTheme} from 'styled-components';

import {isNotEmpty, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {AvailableCar} from '../../../types/AvailableCar';
import {PriceReportType} from '../../../types/PriceReportType';
import {featureFlags} from '../../../utils/featureFlags';
import {getPrice} from '../../../utils/getPrice';
import {DEFAULT_VALUE} from '../consts/defaultValue';
import {PriceChartLabel} from './components/PriceChartLabel';
import {calculateChartDomain} from './utils/calculateChartDomain';
import {getPriceChanges} from './utils/getPriceChanges';

interface PriceChangesPopperProps extends TestIdProps {
  vehicle: AvailableCar;
  priceReport: PriceReportType;
  popperPlacement?: Placement;
}

export function PriceChangesPopper(props: PriceChangesPopperProps) {
  const formatCurrency = useFormatCurrency();
  const formatDate = useDateTimeFormatter();
  const theme = useTheme();
  const {openPopper, closePopper, referenceRef, popperProps, Popper} = useAnimatedPopper({
    placement: props.popperPlacement,
  });

  const vehiclePrice = getPrice(props.vehicle, props.priceReport);
  const currency = props.priceReport.sellingPrice.recommended.currency;
  const firstScrapedPrice = props.vehicle.first_scraped_price_with_vat ?? vehiclePrice;
  const priceChanges = getPriceChanges(props.vehicle);
  const chartData = isNotEmpty(priceChanges)
    ? [...priceChanges, {date: new Date().getTime(), price: vehiclePrice}]
    : [];
  const chartDomain = calculateChartDomain(chartData, currency);
  const discount = firstScrapedPrice - vehiclePrice;
  const shouldShowPriceChanges = priceChanges.length > 0;
  const nDaysText = isNotNil(props.vehicle.days_on_stock)
    ? i18n.t('general.labels.nDaysInAdvertisement', {
        count: props.vehicle.days_on_stock,
      })
    : DEFAULT_VALUE;

  return (
    <>
      <Show when={shouldShowPriceChanges} whenFeatureEnabled={featureFlags.PR_PRICE_CHANGES}>
        <PriceChangeBox
          ref={referenceRef}
          $colorScheme={discount >= 0 ? 'orange' : 'red'}
          onMouseEnter={() => openPopper()}
          onMouseLeave={() => closePopper()}
          onFocus={() => openPopper()}
          onBlur={() => closePopper()}
          onClick={() => openPopper()}
          role="button"
          tabIndex={0}
          aria-label={i18n.t('general.labels.priceChanges')}
        >
          <Icon
            value={discount >= 0 ? 'action/trending_down' : 'action/trending_up'}
            size={3}
            color="general.white"
          />
        </PriceChangeBox>
      </Show>

      <Popper {...popperProps}>
        <Box width={['100vw', 133]} padding={[4, 0]}>
          <Card>
            <VStack spacing={4} width="100%">
              <HStack spacing={1} align="flex-start" justify="space-between" width="100%">
                <VStack spacing={1}>
                  <Heading size={4} color="primary">
                    {i18n.t('general.labels.priceChanges')}
                  </Heading>
                  <Text size="xSmall" color="secondary">
                    {nDaysText}
                  </Text>
                </VStack>
                <Flag
                  colorScheme={discount >= 0 ? 'orange' : 'red'}
                  size="small"
                  label={
                    discount >= 0
                      ? i18n.t('general.labels.discount', {
                          discount: formatCurrency(discount, currency),
                        })
                      : i18n.t('general.labels.priceIncrease', {
                          increase: formatCurrency(-(discount ?? 0), currency),
                        })
                  }
                  data-testid={suffixTestId('discount', props)}
                />
              </HStack>
              <Box width="100%" height={30}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    /* CSS is needed to allow label overflow from the chart */
                    /* eslint-disable-next-line react/forbid-component-props */
                    css={`
                      .recharts-surface {
                        overflow: visible;
                      }
                    `}
                  >
                    <defs>
                      <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0%"
                          stopColor={theme.colors.general.accent}
                          stopOpacity={0.5}
                        />
                        <stop
                          offset="100%"
                          stopColor={theme.colors.general.accent}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      stroke={theme.colors.palettes.neutral[30][100]}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      axisLine={false}
                      tickLine={false}
                      tick={{fontSize: 12, fill: theme.colors.text.secondary}}
                      ticks={chartData.map((item) => item.date)}
                      tickFormatter={(value) => formatDate('dateShort', value)}
                    />
                    <YAxis
                      dataKey="price"
                      domain={[chartDomain.min, chartDomain.max]}
                      axisLine={false}
                      tickLine={false}
                      tick={{fontSize: 12, fill: theme.colors.text.secondary}}
                      tickFormatter={(value) => formatCurrency(value, currency) ?? ''}
                      width={72}
                    />
                    <Area
                      type="stepAfter"
                      dataKey="price"
                      stroke={theme.colors.general.accent}
                      fill="url(#priceGradient)"
                      strokeWidth={1}
                      isAnimationActive={false}
                      label={({
                        viewBox,
                        index,
                      }: {
                        viewBox: {x: number; y: number};
                        index: number;
                      }) => (
                        <PriceChartLabel
                          viewBox={viewBox}
                          index={index}
                          totalPoints={chartData.length}
                          firstPrice={firstScrapedPrice}
                          lastPrice={vehiclePrice}
                          currency={currency}
                        />
                      )}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </VStack>
          </Card>
        </Box>
      </Popper>
    </>
  );
}

type PriceChangeBoxProps = {
  $colorScheme: 'orange' | 'red';
};

// General Box component doesn't allow to set 2px padding
// eslint-disable-next-line eag/no-css-property
const PriceChangeBox = styled.div<PriceChangeBoxProps>`
  background-color: ${(props) => props.theme.colors.palettes[props.$colorScheme][60][100]};
  border-radius: ${(props) => props.theme.radii.xSmall};
  padding: 2px;

  &:hover,
  &:focus-visible {
    background-color: ${(props) => props.theme.colors.palettes[props.$colorScheme][70][100]};
  }
`;
